// DuelLogic.js
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase'; // if not already

/**
 * Enhanced shuffle function using multiple randomization techniques for card games
 * Combines Fisher-Yates shuffle with additional entropy sources for better randomization
 */
export function shuffleArray(array) {
  if (!array || array.length <= 1) return array;
  
  const copy = [...array];
  const length = copy.length;
  
  // Enhanced Fisher-Yates shuffle with multiple entropy sources
  for (let i = length - 1; i > 0; i--) {
    // Combine multiple entropy sources for better randomization
    const entropy1 = Math.random();
    const entropy2 = performance.now() % 1; // Microsecond precision
    const entropy3 = Date.now() % 1; // Millisecond precision
    
    // Combine entropies with XOR for better distribution
    const combinedEntropy = entropy1 ^ entropy2 ^ entropy3;
    
    // Generate random index with enhanced entropy
    const j = Math.floor(combinedEntropy * (i + 1));
    
    // Swap elements
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  
  return copy;
}

/**
 * Test shuffle quality by analyzing distribution patterns
 * Useful for debugging and ensuring fair shuffling
 */
export function testShuffleQuality(array, shuffleFunction = shuffleArray, iterations = 10000) {
  if (!array || array.length < 2) return null;
  
  const length = array.length;
  const positionCounts = Array.from({ length }, () => Array.from({ length }, () => 0));
  
  // Run multiple shuffles and count card positions
  for (let i = 0; i < iterations; i++) {
    const shuffled = shuffleFunction([...array]);
    shuffled.forEach((card, position) => {
      const originalIndex = array.findIndex(c => c.id === card.id);
      if (originalIndex !== -1) {
        positionCounts[originalIndex][position]++;
      }
    });
  }
  
  // Calculate distribution statistics
  const expectedCount = iterations / length;
  let totalDeviation = 0;
  
  for (let i = 0; i < length; i++) {
    for (let j = 0; j < length; j++) {
      const deviation = Math.abs(positionCounts[i][j] - expectedCount);
      totalDeviation += deviation;
    }
  }
  
  const averageDeviation = totalDeviation / (length * length);
  const qualityScore = Math.max(0, 100 - (averageDeviation / expectedCount) * 100);
  
  return {
    qualityScore: Math.round(qualityScore),
    averageDeviation: Math.round(averageDeviation),
    expectedCount: Math.round(expectedCount),
    positionCounts,
    iterations
  };
}

/**
 * Tournament-grade shuffle function for competitive play
 * Uses cryptographic-grade randomization when available
 */
export function tournamentShuffle(array) {
  if (!array || array.length <= 1) return array;
  
  const copy = [...array];
  const length = copy.length;
  
  // Try to use crypto.getRandomValues if available (more secure)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    try {
      // Use crypto.getRandomValues for better entropy
      const randomValues = new Uint32Array(length);
      crypto.getRandomValues(randomValues);
      
      // Fisher-Yates with crypto random
      for (let i = length - 1; i > 0; i--) {
        const j = randomValues[i] % (i + 1);
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      
      return copy;
    } catch (error) {
      console.warn('Crypto shuffle failed, falling back to enhanced shuffle:', error);
    }
  }
  
  // Fallback to enhanced shuffle
  return shuffleArray(copy);
}


export function loadDeckFromStorage() {
  const savedDecks = JSON.parse(localStorage.getItem('savedDecks'));
  // Use the correct localStorage key that DeckBuilderPage uses
  const selectedDeckIndex = JSON.parse(localStorage.getItem('lastSelectedDeckIndex'));

  if (savedDecks && selectedDeckIndex !== null && savedDecks[selectedDeckIndex]) {
    const deck = savedDecks[selectedDeckIndex];
    // Use the enhanced shuffle function instead of biased sort
    const shuffled = shuffleArray([...deck.main]);
    return {
      main: shuffled.slice(5),
      extra: deck.extra || [],
      side: deck.side || [],
      hand: shuffled.slice(0, 5),
    };
  }

  return null;
}

export const handleDeckAction = ({
  action,
  mainDeck,
  setMainDeck,
  setHand,
  setMyGYZ,
  hand,
  setMyBZ,
  extraDeck,
  myGYZ,
  myBZ,
  roomId,
  playerRole,
  fieldMap
}) => {
  if (mainDeck.length === 0) return;

  const topCard = mainDeck[0];
  const rest = mainDeck.slice(1);

  switch (action) {
    case 'View':
      console.log('Deck:', mainDeck);
      break;

    case 'Banish FD': {
      const updatedMain = rest;
      const updatedBZ = [...myBZ, { ...topCard, isFaceDown: true }];

      setMainDeck(updatedMain);
      setMyBZ(updatedBZ);

      syncGameState({
        fieldMap,
        hand,
        mainDeck: updatedMain,
        extraDeck,
        gy: myGYZ,
        bz: updatedBZ,
        roomId,
        playerRole
      });
      break;
    }

    case 'Banish T.': {
      const updatedMain = rest;
      const updatedBZ = [...myBZ, topCard];

      setMainDeck(updatedMain);
      setMyBZ(updatedBZ);

      syncGameState({
        fieldMap,
        hand,
        mainDeck: updatedMain,
        extraDeck,
        gy: myGYZ,
        bz: updatedBZ,
        roomId,
        playerRole
      });
      break;
    }

    case 'Mill': {
      const updatedMain = rest;
      const updatedGY = [...myGYZ, topCard];

      setMainDeck(updatedMain);
      setMyGYZ(updatedGY);

      syncGameState({
        fieldMap,
        hand,
        mainDeck: updatedMain,
        extraDeck,
        gy: updatedGY,
        bz: myBZ,
        roomId,
        playerRole
      });
      break;
    }

    case 'Shuffle': {
      const shuffled = shuffleArray(mainDeck);
      setMainDeck(shuffled);

      syncGameState({
        fieldMap,
        hand,
        mainDeck: shuffled,
        extraDeck,
        gy: myGYZ,
        bz: myBZ,
        roomId,
        playerRole
      });
      break;
    }

    case 'Draw': {
      const updatedHand = [...hand, topCard];
      const updatedMain = rest;

      setHand(updatedHand);
      setMainDeck(updatedMain);

      syncGameState({
        fieldMap,
        hand: updatedHand,
        mainDeck: updatedMain,
        extraDeck,
        gy: myGYZ,
        bz: myBZ,
        roomId,
        playerRole
      });
      break;
    }

    default:
      break;
  }
};


export const syncGameState = async ({

  fieldMap,
  hand,
  mainDeck,
  extraDeck,
  gy,
  bz,
  roomId,
  playerRole
}) => {
  if (!roomId || !playerRole) return;
  if (!db) {
    console.error('🔥 Firestore DB is undefined!');
    return;
  }


  const roomRef = doc(db, 'duelRooms', roomId);

  try {
    await updateDoc(roomRef, {
      [`gameState.${playerRole}.fieldMap`]: fieldMap,
      [`gameState.${playerRole}.hand`]: hand,
      [`gameState.${playerRole}.mainDeck`]: mainDeck,
      [`gameState.${playerRole}.extraDeck`]: extraDeck,
      [`gameState.${playerRole}.gy`]: gy,
      [`gameState.${playerRole}.bz`]: bz,
      'gameState.updatedAt': serverTimestamp()
    });
    console.log('✅ syncGameState success');
  } catch (err) {
    console.error('🔥 syncGameState failed:', err);
  }
};

